import { Container, Factory } from "inversify";
import { QueryClient } from "@tanstack/react-query";
import type { IQueryClient } from "@workspace/types";
import { LandingServiceID } from "./symbols";
import { LandingSessionStore } from "./landing-session.store";
import { LandingRootStore, ILandingRootStore } from "./landing-root.store";
import { RemainingTime } from "./timer";
import { LandingCoachStore } from "./coach.store";
import { StoreQueryClient } from "@workspace/core";
import { DemoDeviceCheckStore } from "./demo-device-check.store";
import { DemoClockStore } from "./demo-clock.store";
import { DemoSignalsStore } from "./demo-signals.store";
import { DemoRecapStore } from "./demo-recap.store";
import { DemoCallControlStore } from "./demo-call-control.store";
import type { IRemainingTime } from "@/lib/types/training";
import type { ILandingSessionManager } from "@/lib/types/store";
import type { ICoachManager } from "@/lib/types/coach";
import type {
  IDemoDeviceCheckManager,
  IDemoClockManager,
  IDemoSignalsManager,
  IDemoRecapManager,
  IDemoCallControlManager,
} from "@/lib/types/demo-session";

const container = new Container({
  defaultScope: "Singleton",
});

container
  .bind<ILandingSessionManager>(LandingServiceID.SessionStore)
  .to(LandingSessionStore);

container
  .bind<ILandingRootStore>(LandingServiceID.RootStore)
  .to(LandingRootStore);

container.bind<IRemainingTime>(LandingServiceID.Timer).to(RemainingTime);

container
  .bind<ICoachManager>(LandingServiceID.CoachStore)
  .to(LandingCoachStore);

container
  .bind<IDemoDeviceCheckManager>(LandingServiceID.DemoDeviceCheckStore)
  .to(DemoDeviceCheckStore);

container
  .bind<IDemoClockManager>(LandingServiceID.DemoClockStore)
  .to(DemoClockStore);

container
  .bind<IDemoSignalsManager>(LandingServiceID.DemoSignalsStore)
  .to(DemoSignalsStore);

container
  .bind<IDemoRecapManager>(LandingServiceID.DemoRecapStore)
  .to(DemoRecapStore);

container
  .bind<IDemoCallControlManager>(LandingServiceID.DemoCallControlStore)
  .to(DemoCallControlStore);

const landingSharedQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

container
  .bind<IQueryClient>(LandingServiceID.QueryClient)
  .to(StoreQueryClient);

container
  .bind<Factory<IQueryClient>>(LandingServiceID.QueryClientFactory)
  .toFactory(
    (context) =>
      <TQueryFnData = unknown>(
        queryOptions: IQueryClient<TQueryFnData>["queryOptions"],
        queryClient?: QueryClient,
      ) => {
        const client = context.get<IQueryClient<TQueryFnData>>(
          LandingServiceID.QueryClient,
        );
        client.queryOptions = queryOptions;
        client.queryClient = queryClient || landingSharedQueryClient;
        return client;
      },
  );

export { container, LandingServiceID };
